import type { ExtractedClaim, ExtractedEvidence } from "@/lib/outline-import-types";

interface HighlightSpan {
  start: number;
  end: number;
  className: string;
  claimTmpId: string;
}

function classForEvidence(ev: ExtractedEvidence): string {
  const m = ev.match;
  if (!m) return "bg-gray-100";
  if (m.status === "matched" && m.confidence === "high" && m.alternatives.length === 0) {
    return "bg-green-200/70 dark:bg-green-800/60 text-foreground";
  }
  if (m.status === "matched") return "bg-amber-200/70 dark:bg-amber-800/60 text-foreground";
  return "bg-yellow-300/70 dark:bg-yellow-700/60 text-foreground";
}

interface Props {
  proseText: string;
  claims: ExtractedClaim[];
  onClickHighlight: (claimTmpId: string) => void;
}

export default function ProsePane({ proseText, claims, onClickHighlight }: Props) {
  const spans: HighlightSpan[] = [];
  for (const c of claims) {
    for (const ev of c.evidence) {
      if (!ev.source_span) continue;
      spans.push({
        start: ev.source_span[0],
        end: ev.source_span[1],
        className: classForEvidence(ev),
        claimTmpId: c.tmp_id,
      });
    }
  }
  spans.sort((a, b) => a.start - b.start);

  const chunks: React.ReactNode[] = [];
  let cursor = 0;
  spans.forEach((s, i) => {
    if (s.start < cursor) return; // skip overlaps
    if (s.start > cursor) chunks.push(<span key={`t-${i}`}>{proseText.slice(cursor, s.start)}</span>);
    chunks.push(
      <mark
        key={`m-${i}`}
        className={`${s.className} cursor-pointer px-0.5`}
        onClick={() => onClickHighlight(s.claimTmpId)}
      >
        {proseText.slice(s.start, s.end)}
      </mark>
    );
    cursor = s.end;
  });
  if (cursor < proseText.length) chunks.push(<span key="tail">{proseText.slice(cursor)}</span>);

  return (
    <div className="overflow-y-auto h-full p-4 whitespace-pre-wrap font-mono text-sm">
      {chunks}
    </div>
  );
}
